import React from "react";
import {useTranslation} from "react-i18next";
import {InputText} from "primereact/inputtext";
import {Button} from "primereact/button";
import {Message} from "primereact/message";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {AppContext} from "../../../HomePage";
import {EmailCampaignService} from "../../../service/EmailCampaignService";

// Admin view of the unsubscribe suppression list: search by email, paginate,
// and remove a single address (re-subscribe) via the per-row action.
export default function EmailCampaignSuppression({active}) {
    const {t} = useTranslation();
    const {toast, setError} = React.useContext(AppContext);
    const service = React.useMemo(() => new EmailCampaignService(), []);

    const [query, setQuery] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [items, setItems] = React.useState([]);
    const [total, setTotal] = React.useState(0);
    const [page, setPage] = React.useState(1);
    const [pageSize] = React.useState(50);
    const [loading, setLoading] = React.useState(false);

    const load = React.useCallback((pageNum, q) => {
        setLoading(true);
        service.listSuppressions({page: pageNum, page_size: pageSize, q: q || ''})
            .then((data) => {
                setItems(data.items || []);
                setTotal(data.total || 0);
                setPage(data.page || pageNum);
            })
            .catch((e) => {
                setError(e);
                setError(null);
            })
            .finally(() => setLoading(false));
    }, [service, pageSize, setError]);

    // Load on tab open and whenever the search query changes (debounced).
    React.useEffect(() => {
        if (!active) return undefined;
        const handle = setTimeout(() => load(1, query), 300);
        return () => clearTimeout(handle);
    }, [active, query, load]);

    const doRemove = (address) => {
        const target = (address || '').trim();
        if (!target) return;
        setBusy(true);
        service.removeSuppression(target)
            .then((data) => {
                toast.current.show({
                    severity: 'success',
                    summary: t('email_campaign.suppression_removed', {count: (data && data.removed) || 0}),
                });
                load(page, query);
            })
            .catch((e) => {
                setError(e);
                setError(null);
            })
            .finally(() => setBusy(false));
    };

    const dateBody = (row) => row.created_at ? new Date(row.created_at).toLocaleString() : '';
    const actionBody = (row) =>
        <Button icon="pi pi-user-plus" className="p-button-text" disabled={busy}
                tooltip={t('email_campaign.suppression_remove')}
                onClick={() => doRemove(row.email)}/>;

    return (
        <div className="p-fluid">
            <Message severity="info" text={t('email_campaign.suppression_hint')}/>

            <div className="p-field" style={{marginTop: '1em', maxWidth: '420px'}}>
                <span className="p-input-icon-left">
                    <i className="pi pi-search"/>
                    <InputText value={query} placeholder={t('email_campaign.suppression_search')}
                               onChange={(e) => setQuery(e.target.value)}/>
                </span>
            </div>

            <DataTable value={items} loading={loading} paginator rows={pageSize}
                       totalRecords={total} lazy first={(page - 1) * pageSize}
                       onPage={(e) => load(Math.floor(e.first / pageSize) + 1, query)}
                       dataKey="email" emptyMessage={t('email_campaign.suppression_empty')}>
                <Column field="email" header="Email"/>
                <Column header={t('email_campaign.suppression_date')} body={dateBody}/>
                <Column body={actionBody} style={{width: '4rem'}}/>
            </DataTable>
        </div>
    );
}
